import { motion } from "framer-motion";
import {
	CalendarIcon,
	LinkIcon,
	MapPin,
	Navigation,
	Users,
	Utensils,
} from "lucide-react";
import { useFormContext } from "react-hook-form";
import { cn } from "~/lib/utils";
import { CoverImage } from "./cover-image";
import { MapPreview } from "./map-preview";
import {
	type EventFormData,
	previewDateFormatter,
	previewTimeFormatter,
} from "./utils";

export function Preview({ isDialog }: { isDialog?: boolean }) {
	const form = useFormContext<EventFormData>();
	const watchedFields = form.watch();
	const coverImageUrl = form.watch("coverImageUrl");

	return (
		<div className="flex flex-col overflow-y-auto w-full h-full p-6 md:overflow-visible">
			{/* Cover Image Preview */}
			{!isDialog ? (
				<CoverImage isPreview={isDialog} className="mb-4 shrink-0" />
			) : null}

			{coverImageUrl && isDialog ? (
				<img
					src={coverImageUrl}
					alt={watchedFields.title || "Cover Image"}
					className="mb-8 shrink-0 h-[200px] md:size-[300px] object-cover rounded-xl"
				/>
			) : null}

			{/* Event Details Preview */}
			<div className="space-y-8 md:pb-4">
				{/* Event Type and Title Section */}
				<div className="space-y-4">
					<motion.h1
						className="text-[2.5rem] font-bold text-white break-words leading-tight  tracking-wide font-heading"
						animate={{ opacity: 1 }}
						key={watchedFields.title}
					>
						{watchedFields.title || "Untitled Event"}
					</motion.h1>

					{watchedFields.description ? (
						<motion.p
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							className="text-white/80 whitespace-pre-wrap break-words text-lg leading-relaxed"
						>
							{watchedFields.description}
						</motion.p>
					) : (
						<p className="text-white/80 whitespace-pre-wrap break-words text-lg leading-relaxed opacity-50">
							No description provided for this event
						</p>
					)}
				</div>

				{/* Event Details Section */}
				<div className="space-y-4">
					<h2 className="text-sm font-medium text-white/40 uppercase tracking-wider mb-4">
						Event Details
					</h2>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="flex items-center gap-3.5 text-white/80 bg-white/5 px-4 py-3 rounded-lg hover:bg-white/[0.07] transition-colors"
					>
						<CalendarIcon className="h-5 w-5 flex-shrink-0 text-accent/80" />
						{watchedFields.startDate ? (
							<div className="flex items-center gap-1.5">
								<span>
									{previewDateFormatter.format(
										new Date(watchedFields.startDate),
									)}
								</span>
								{watchedFields.startTime && (
									<span className="text-white/60">
										at{" "}
										{previewTimeFormatter.format(
											new Date(`2000-01-01T${watchedFields.startTime}`),
										)}
									</span>
								)}
							</div>
						) : (
							<p className="text-white/80 whitespace-pre-wrap break-words text-sm leading-relaxed opacity-50">
								No date provided
							</p>
						)}
					</motion.div>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="flex flex-col bg-white/5 rounded-lg overflow-hidden hover:bg-white/[0.07] transition-colors"
					>
						{!watchedFields.locationCoordinates ? (
							<div className="flex items-center gap-3.5 px-4 py-3 text-white/80 overflow-hidden">
								<MapPin className="h-5 w-5 flex-shrink-0 text-accent/80" />
								<div className="flex-1">
									<span
										className={cn(
											"break-words",
											!watchedFields.locationAddress && "opacity-50 text-sm",
										)}
									>
										{watchedFields.locationAddress || "Location TBD"}
									</span>
								</div>
							</div>
						) : null}

						{/* Map preview when coordinates are available */}
						{watchedFields.locationCoordinates && (
							<div className="w-full">
								<MapPreview
									coordinates={watchedFields.locationCoordinates}
									className="w-full h-[180px]"
								/>
								<div className="px-4 py-2 flex justify-between items-center bg-white/5">
									<span className="text-xs text-white/50">Map location</span>
									<div className="flex gap-3">
										{watchedFields.locationUrl && (
											<a
												href={watchedFields.locationUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="text-xs text-accent flex items-center gap-1.5 hover:underline"
											>
												<LinkIcon className="h-3 w-3" />
												View on Maps
											</a>
										)}
										<a
											href={`https://www.google.com/maps/dir/?api=1&destination=${watchedFields.locationCoordinates}`}
											target="_blank"
											rel="noopener noreferrer"
											className="text-xs text-accent flex items-center gap-1.5 hover:underline"
										>
											<Navigation className="h-3 w-3" />
											Get directions
										</a>
									</div>
								</div>
							</div>
						)}
					</motion.div>
					{watchedFields.collectDietaryRestrictions ? (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							className="flex items-center gap-3.5 text-white/80 bg-white/5 px-4 py-3 rounded-lg hover:bg-white/[0.07] transition-colors"
						>
							<Utensils className="size-5 flex-shrink-0 text-accent/80" />
							<span>Guests can specify their dietary restrictions</span>
						</motion.div>
					) : null}
				</div>

				{/* Guest Settings Section */}
				<div className="space-y-4">
					<h2 className="text-sm font-medium text-white/40 uppercase tracking-wider mb-4">
						Guest Information
					</h2>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="flex items-center gap-3.5 text-white/80 bg-white/5 px-4 py-3 rounded-lg hover:bg-white/[0.07] transition-colors"
					>
						<Users className="h-5 w-5 flex-shrink-0 text-accent/80" />
						<span
							className={cn(
								"block text-white/60 mt-1",
								!watchedFields.guestListVisibility && "opacity-50 text-sm",
							)}
						>
							Guest list{" "}
							{watchedFields.guestListVisibility ? "visible to" : "hidden from"}{" "}
							attendees
						</span>
					</motion.div>

					<div className="space-y-2.5 px-4 py-3">
						{watchedFields.guestLimit && (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								className="text-sm text-white/60 flex items-center gap-2"
							>
								<div className="w-1 h-1 rounded-full bg-white/40" />
								Limited to {watchedFields.guestLimit} guests
							</motion.div>
						)}
						{watchedFields.allowPlusOne && (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								className="text-sm text-white/60 flex items-center gap-2"
							>
								<div className="w-1 h-1 rounded-full bg-white/40" />
								Plus ones welcome
							</motion.div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
